/* eslint-disable no-console */
// A console handle for the one question that keeps coming up when images stay empty:
// "where exactly does the chain break?". The chain has four links, and each one fails silently in
// its own way, which is why guessing from the outside rarely works:
//
//   1. Configuration   - VITE_APP_ASSETS_STORAGE/_CONTAINER set? Without them NO asset URL is ever
//                        built, so the network tab stays empty and there is nothing to see.
//   2. Index data      - does the delivered index actually carry an image assetId per article?
//                        Real STIBO data carries it in /catalog/thumbnails, not on the index row -
//                        an empty thumbnails response means no image URL, again with no request.
//   3. SAS token       - is /azure/GetStorageContainerSasToken reachable, authorized, and does it
//                        answer in the expected shape?
//   4. The blob itself - does the signed URL actually return the file (403 = token/permission,
//                        404 = never published, 200 = it works and the problem is elsewhere)?
//
// Everything here is read-only diagnostics against what the running app already has. It is
// deliberately available in the deployed DEV build too, not just under `vite dev`: the whole point
// is to debug a real environment (private container, real login) that a local run cannot reproduce.
//
// Usage in the browser console:
//   pih.help()
//   pih.config()
//   await pih.token()               // fetch/renew and show the result
//   await pih.index()               // how many index rows have an image URL at all
//   await pih.check()               // the whole chain, with a verdict (or pih.check('<assetId>'))
//   pih.url('<assetId>')            // the signed URL, ready to open in a new tab
//   await pih.list('assets/tn/')    // what is REALLY in the container (names, extensions)
//   pih.trace(true)                 // live commentary: token fetches and every URL the app builds
//   pih.lastUrls()                  // the URLs it built most recently

import { apiCalls, apiStats, isApiLog, setApiLog, type ApiCallRecord } from '../apiLog';
import { isLoginEnabled } from '../authMode';
import {
    assetsContainer,
    assetsRoot,
    assetsStorage,
    currentSasToken,
    ensureSasToken,
    isAssetTrace,
    isSasRequired,
    recentAssetUrls,
    sasEndpointUrl,
    sasTokenExpiresAt,
    setAssetTrace,
    signAssetUrl,
    type AssetUrlRecord
} from './blobAssets';
import { buildIndexByArtNr, buildTreeIndex, categoryCounts } from './engine';

import type { CatalogProvider } from './provider';

type AssetKind = 'image' | 'thumbnail';

const DEFAULT_LANGUAGE = 'de';

const shorten = (value: string, max = 120) => (value.length > max ? `${value.slice(0, max)}…` : value);

/** Loads the URL exactly the way an <img> would - cross-origin, no CORS involved. */
const probeImage = (url: string): Promise<{ loaded: boolean; width?: number }> =>
    new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve({ loaded: true, width: img.naturalWidth });
        img.onerror = () => resolve({ loaded: false });
        img.src = url;
    });

/**
 * Same URL through fetch() - this DOES need CORS rules on the container, so a failure here while
 * probeImage succeeds means "CORS missing", not "asset missing". When it does get through, Azure's
 * XML error body names the real cause (AuthenticationFailed / BlobNotFound / AuthorizationPermissionMismatch).
 */
const probeFetch = async (url: string): Promise<Record<string, unknown>> => {
    try {
        const response = await fetch(url);
        return {
            status: response.status,
            statusText: response.statusText,
            body: response.ok ? undefined : shorten((await response.text()).replace(/\s+/g, ' '), 300)
        };
    } catch (cause) {
        return {
            error: String(cause),
            hint: 'fetch() blocked - most likely no CORS rule on the container. An <img> does not need one, so check the image probe above.'
        };
    }
};

/**
 * Blob names actually present under a prefix, straight from the storage account.
 *
 * This is the difference between "the asset is not there" and "we are asking for the wrong name":
 * a 404 says only that THIS name does not exist, while the listing shows what does - including the
 * real file extensions, which the catalog JSON never carries (see provider.ts's
 * EXTENSION_BY_MIME_TYPE, which has to guess them) and whether assets/tn/ was published at all
 * (the thumbnail stream is optional in the converter pipeline).
 *
 * Uses the same container SAS as every image: `sp=rl` includes list permission, so no extra
 * credentials are involved. Needs a CORS rule like any other fetch (unlike an <img>).
 */
const listUrlFor = (prefix: string, max: number, sas: string | null): string =>
    `${assetsRoot}?restype=container&comp=list&prefix=${encodeURIComponent(prefix)}` +
    `&maxresults=${max}${sas ? `&${sas}` : ''}`;

const listBlobs = async (prefix: string, max: number): Promise<{ names: string[]; error?: string; url?: string }> => {
    const sas = await ensureSasToken();
    if (isSasRequired && !sas) {
        return { names: [], error: 'no SAS token - run `await pih.token()` first.' };
    }
    if (sas && !(new URLSearchParams(sas).get('sp') ?? '').includes('l')) {
        return { names: [], error: `token has no list permission (sp=${new URLSearchParams(sas).get('sp')}).` };
    }
    const url = listUrlFor(prefix, max, sas);
    try {
        const response = await fetch(url);
        if (!response.ok) {
            return { names: [], url, error: `${response.status} ${response.statusText} - ${shorten(await response.text(), 200)}` };
        }
        const xml = new DOMParser().parseFromString(await response.text(), 'application/xml');
        return { names: [...xml.querySelectorAll('Blob > Name')].map((node) => node.textContent ?? ''), url };
    } catch (cause) {
        // Without a CORS rule the browser throws away a response the storage account did send (the
        // network tab shows the real status next to ERR_FAILED). A top-level navigation is not
        // subject to CORS at all, so the same URL opened in a new tab still answers - hence the
        // hint, and `pih.listUrl()` for exactly that.
        return {
            names: [],
            url,
            error: `${String(cause)} - fetch blocked, most likely a missing CORS rule on the container. Open the URL below in a new tab instead (a tab navigation is not subject to CORS).`
        };
    }
};

// Extensions the converter/STIBO delivery could plausibly have used. The catalog JSON carries only
// the bare assetId, so provider.ts guesses one from the mimeType - and a wrong guess looks exactly
// like a missing file (404).
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.gif', '.tif', '.tiff', '.bmp'];

/** The distinct file extensions in a set of blob names - the quickest way to spot a wrong guess. */
const extensionsOf = (names: string[]): string[] => [
    ...new Set(names.map((name) => name.slice(name.lastIndexOf('.'))).filter((ext) => ext.length > 1 && ext.length < 8))
];

export interface AssetDebugApi {
    help: () => void;
    config: () => Record<string, unknown>;
    token: (force?: boolean) => Promise<Record<string, unknown>>;
    url: (assetId: string, kind?: AssetKind, mimeType?: string) => string | null | undefined;
    probe: (assetId: string, kind?: AssetKind) => Promise<Record<string, unknown>>;
    index: (language?: string) => Promise<Record<string, unknown>>;
    check: (assetId?: string, language?: string) => Promise<Record<string, unknown>>;
    /** Lists what the container really holds under a prefix ('assets/', 'assets/tn/', ...). */
    list: (prefix?: string, max?: number) => Promise<Record<string, unknown>>;
    /** The same listing URL to open in a new tab - works even without a CORS rule. */
    listUrl: (prefix?: string, max?: number) => Promise<string>;
    /** Tries every plausible extension in assets/ and assets/tn/ via <img> - needs no CORS. */
    find: (assetId: string) => Promise<Record<string, unknown>>;
    /** Takes the first N image assetIds the catalog delivers and checks which of them exist. */
    sample: (count?: number, language?: string) => Promise<Record<string, unknown>>;
    /** Live commentary in the console: token fetches, renewals, and every URL the app builds. */
    trace: (on?: boolean) => boolean;
    /** The URLs the app built most recently - assetId, mimeType, directory, extension. */
    lastUrls: (count?: number) => AssetUrlRecord[];
    /** The raw catalog provider - `await pih.catalog.getArticle('586214000')` etc. */
    catalog: CatalogProvider;
    /** API log on/off: one console line per backend call with status, duration and size. */
    api: (on?: boolean) => boolean;
    /** Calls per route: count, average/max duration, errors. */
    apiStats: () => Record<string, unknown>[];
    /** The last backend calls as a table. */
    apiCalls: (count?: number) => ApiCallRecord[];
    /** What the loaded catalog holds and why the grid shows the number it does. */
    stats: (language?: string) => Promise<Record<string, unknown>>;
}

const buildApi = (provider: CatalogProvider): AssetDebugApi => {
    const assetUrl = (assetId: string, kind: AssetKind, mimeType = 'image/jpeg') =>
        kind === 'thumbnail' ? provider.getThumbnailUrl(assetId, mimeType) : provider.getAssetUrl(assetId, mimeType);

    const config = () => {
        const token = currentSasToken();
        const summary = {
            storage: assetsStorage || '(not configured - VITE_APP_ASSETS_STORAGE)',
            container: assetsContainer || '(not configured - VITE_APP_ASSETS_CONTAINER)',
            assetsRoot: assetsRoot || '(empty - no asset URL is built at all)',
            needsSasToken: isSasRequired,
            sasEndpoint: sasEndpointUrl,
            loginEnabled: isLoginEnabled,
            tokenPresent: Boolean(token),
            tokenExpires: sasTokenExpiresAt() ? new Date(sasTokenExpiresAt()).toISOString() : '(none yet)'
        };
        console.table(summary);
        return summary;
    };

    const token = async (force = false) => {
        if (force) {
            // Nothing resets the cached token from outside on purpose - a forced renewal simply
            // means asking again once the current one is close enough to its expiry; for a hard
            // reload of the token, reload the page.
            console.info('force only re-asks when the current token is (nearly) expired - reload the page for a hard reset.');
        }
        const value = await ensureSasToken();
        const result = value
            ? {
                  ok: true,
                  expires: new Date(sasTokenExpiresAt()).toISOString(),
                  permissions: new URLSearchParams(value).get('sp'),
                  resource: new URLSearchParams(value).get('sr'),
                  token: value
              }
            : {
                  ok: false,
                  hint: isSasRequired
                      ? `No token. Check the console error above and call ${sasEndpointUrl} manually - 401 means the login has no catalog scope, 403 means the app registration lacks the role, 404 means the endpoint is not deployed.`
                      : 'No token needed for this configuration (public container / offline bundle) - if images are missing, the cause is elsewhere.'
              };
        console.table(result);
        return result;
    };

    const url = (assetId: string, kind: AssetKind = 'image', mimeType = 'image/jpeg') =>
        signAssetUrl(assetUrl(assetId, kind, mimeType));

    const probe = async (assetId: string, kind: AssetKind = 'image') => {
        await ensureSasToken();
        const signed = url(assetId, kind);
        if (!signed) {
            const result = {
                ok: false,
                signedUrl: signed,
                hint:
                    signed === undefined
                        ? 'A token is needed but none has arrived yet - run `await pih.token()` first.'
                        : 'No URL at all - assetsRoot is empty, see pih.config().'
            };
            console.table(result);
            return result;
        }
        const [image, fetched] = await Promise.all([probeImage(signed), probeFetch(signed)]);
        const result = { ok: image.loaded, imageLoaded: image.loaded, imageWidth: image.width, ...fetched, signedUrl: signed };
        console.table(result);
        console.info('Open this URL in a new tab to see Azure\'s own error message:', signed);

        // A 404 means the token was accepted and only this exact name is missing - so the next
        // question is always "then what IS in there?". Answering it right here saves the round trip.
        // A 404 (or an <img> that stays empty) means the token was accepted and only this exact name
        // is missing - so the next question is always "then what IS there?". The extension sweep
        // answers it without CORS; the listing needs a rule and is therefore only attempted second.
        if (fetched.status === 404 || (!image.loaded && fetched.status === undefined)) {
            console.info('Trying the other plausible names for this assetId (via <img>, no CORS needed):');
            await find(assetId);
            const prefix = kind === 'thumbnail' ? 'assets/tn/' : 'assets/';
            await list(prefix, 10);
        }
        return result;
    };

    const list = async (prefix = 'assets/', max = 20) => {
        const { names, error, url } = await listBlobs(prefix, max);
        const result = {
            prefix,
            found: names.length,
            extensions: extensionsOf(names).join(', ') || '(none)',
            error,
            hint: error
                ? 'The listing itself did not get through - see the error. The URL below still works in a new tab.'
                : names.length > 0
                  ? 'These are the real names. Compare them with pih.lastUrls() - same folder, same extension?'
                  : `Nothing under "${prefix}". Try pih.list('') for the container root, or a different prefix.`
        };
        console.table(result);
        if (names.length > 0) {
            console.table(names);
        } else if (url) {
            console.info('Open this in a new tab to see the listing XML (no CORS involved):', url);
        }
        return { ...result, names, url };
    };

    const listUrl = async (prefix = 'assets/', max = 50) => {
        const url = listUrlFor(prefix, max, await ensureSasToken());
        console.info('Open in a new tab (a tab navigation is not subject to CORS):', url);
        return url;
    };

    const find = async (assetId: string) => {
        await ensureSasToken();
        const candidates = ['assets', 'assets/tn'].flatMap((dir) =>
            IMAGE_EXTENSIONS.map((extension) => ({ dir, extension }))
        );
        // <img> instead of fetch on purpose: an image load needs no CORS rule, so this works against
        // a container that blocks every fetch - which is exactly the situation this is written for.
        const probes = await Promise.all(
            candidates.map(async (candidate) => {
                const signed = signAssetUrl(`${assetsRoot}/${candidate.dir}/${encodeURIComponent(assetId)}${candidate.extension}`);
                if (!signed) {
                    return { ...candidate, loaded: false };
                }
                const { loaded, width } = await probeImage(signed);
                return { ...candidate, loaded, width, url: signed };
            })
        );
        const hits = probes.filter((probe) => probe.loaded);
        const result = {
            assetId,
            tried: probes.length,
            found: hits.length,
            where: hits.map((hit) => `${hit.dir}/…${hit.extension}`).join(', ') || '(nothing)',
            hint:
                hits.length > 0
                    ? 'Found it - if the app still shows nothing, it is asking for a different folder/extension (see pih.lastUrls()).'
                    : 'No image under any usual extension. Either this assetId was never published as an image, or it is a document (PDF) - those cannot be probed this way, use pih.listUrl() and open it in a tab.'
        };
        console.table(result);
        if (hits.length > 0) {
            console.table(hits);
        }
        return { ...result, hits };
    };

    /**
     * The assetId out of a URL the provider built - everything after the last slash, minus the
     * extension it guessed from the mimeType.
     */
    const assetIdOf = (url: string): string =>
        decodeURIComponent((url.split('?')[0].split('/').pop() ?? '').replace(/\.[a-z0-9]+$/i, ''));

    const sample = async (count = 10, language = DEFAULT_LANGUAGE) => {
        await ensureSasToken();
        const items = await provider.getIndex(language);
        const withImage = items.filter((item) => item.image).slice(0, count);
        const probes = await Promise.all(
            withImage.map(async (item) => {
                const assetId = assetIdOf(item.image ?? '');
                const [original, thumbnail] = await Promise.all([
                    probeImage(signAssetUrl(item.image) ?? ''),
                    item.imageThumbnailUrl ? probeImage(signAssetUrl(item.imageThumbnailUrl) ?? '') : Promise.resolve({ loaded: false })
                ]);
                return { artNr: item.artNr, assetId, original: original.loaded, thumbnail: thumbnail.loaded };
            })
        );
        const originals = probes.filter((probe) => probe.original).length;
        const thumbnails = probes.filter((probe) => probe.thumbnail).length;
        const result = {
            checked: probes.length,
            originalsFound: `${originals}/${probes.length}`,
            thumbnailsFound: `${thumbnails}/${probes.length}`,
            hint:
                originals === 0
                    ? 'None of the assetIds the catalog delivers exists in the container. The URL scheme is not the problem - catalog data and published assets come from different deliveries (compare the blobs\' upload date with the current converter run).'
                    : originals < probes.length
                      ? 'Some exist, some do not - individual assets were not published (or were deleted).'
                      : thumbnails === 0
                        ? 'Every original is there, no thumbnail is - assets/tn/ was not published for this run. ProductImage falls back to the original, so images show; the 404s per tile are the visible cost.'
                        : 'Everything resolves - if the page still shows no images, the cause is not the asset chain.'
        };
        console.table(result);
        console.table(probes);
        return { ...result, probes };
    };

    const index = async (language = DEFAULT_LANGUAGE) => {
        const items = await provider.getIndex(language);
        const withImage = items.filter((item) => item.image || item.imageThumbnailUrl);
        const result = {
            items: items.length,
            withImageUrl: withImage.length,
            withoutImageUrl: items.length - withImage.length,
            firstWithImage: withImage[0]?.artNr,
            sampleImageUrl: withImage[0]?.image,
            hint:
                withImage.length === 0
                    ? 'No index row carries an image at all - the picture never reaches the browser, so this is a DATA problem (GET /catalog/thumbnails empty?), not a token problem.'
                    : 'Image URLs exist - run pih.probe(<assetId>) or pih.check() to see what the storage answers.'
        };
        console.table(result);
        return result;
    };

    const check = async (assetId?: string, language = DEFAULT_LANGUAGE) => {
        console.group('PIH asset check');
        const configuration = config();
        if (!assetsRoot) {
            console.warn('Step 1 failed: no asset root configured - VITE_APP_ASSETS_STORAGE is empty, so no image URL is ever built.');
            console.groupEnd();
            return { step: 'config', configuration };
        }
        const indexResult = await index(language);
        const tokenResult = isSasRequired ? await token() : { ok: true, hint: 'no token needed' };
        // Falls back to the first index row that actually has an image, so the usual case needs no
        // argument at all.
        const items = await provider.getIndex(language);
        const fallback = items.find((item) => item.image)?.image;
        const target = assetId ?? (fallback ? decodeURIComponent(fallback.split('/').pop()?.replace(/\.[a-z0-9]+$/i, '') ?? '') : undefined);
        const probeResult = target ? await probe(target) : { ok: false, hint: 'nothing to probe - no article with an image found.' };
        console.groupEnd();
        return { configuration, index: indexResult, token: tokenResult, probe: probeResult, probed: target };
    };

    const help = () => {
        console.info(
            [
                'pih.config()                 - storage/container/token status',
                'await pih.token()            - fetch or renew the SAS token',
                'await pih.index()            - do index rows carry image URLs at all?',
                'pih.url(assetId, kind?)      - the signed URL ("image" | "thumbnail")',
                'await pih.probe(assetId)     - load it as <img> AND via fetch',
                'await pih.check()            - all of the above, in order, with a verdict',
                'await pih.list(\'assets/tn/\') - what the container REALLY holds (names, extensions)',
                'await pih.find(assetId)      - tries every plausible extension via <img> (no CORS needed)',
                'await pih.sample(20)         - do the assetIds the catalog delivers exist at all?',
                'await pih.listUrl(\'assets/\') - listing URL to open in a new tab (works without CORS)',
                'pih.trace(true)              - live log: token fetches and every URL the app builds',
                'pih.lastUrls()               - the URLs it built most recently',
                'pih.catalog                  - the raw provider (getIndex/getArticle/...)',
                '',
                'pih.api(true) / pih.api(false) - log every backend call: status, duration, size (remembered)',
                'pih.apiStats()               - per route: calls, avg/max duration, errors',
                'pih.apiCalls(20)             - the last 20 calls as a table',
                '',
                'await pih.stats()            - what the loaded catalog holds: products/parts, in tree or not,',
                '                               counts per top-level node, loaded files with size and time'
            ].join('\n')
        );
    };

    const trace = (on = true) => {
        const enabled = setAssetTrace(on);
        console.info(
            enabled
                ? 'Asset trace on: token fetches, renewals and every built URL are logged from now on. ' +
                      'Navigate or reload to see the index being resolved.'
                : 'Asset trace off.'
        );
        return enabled;
    };

    // Explains the gap between "rows in the index" and "hits in the grid": without a search term
    // the grid lists only type 'product' rows that hang in the selected tree node (engine.ts's
    // selectCatalog). Parts and products without a tree node are reachable through the search only.
    const stats = async (language = DEFAULT_LANGUAGE) => {
        // Taken first: the provider below fetches index and tree once more, which would otherwise
        // show up here as a second load. Only catalog files - the dev server's own /src/app/catalog/
        // modules stay out, and so do the per-article files and assets.
        const files = performance
            .getEntriesByType('resource')
            .map((entry) => entry as PerformanceResourceTiming)
            .filter((timing) => {
                const path = new URL(timing.name).pathname;
                return /^\/(catalog-data|catalog)\//.test(path) && !/\/(articles|assets)\//.test(path);
            })
            .map((timing) => ({
                file: new URL(timing.name).pathname,
                sizeKB: Math.round((timing.decodedBodySize || timing.encodedBodySize) / 1024),
                transferKB: Math.round(timing.transferSize / 1024),
                ms: Math.round(timing.duration)
            }));

        const [index, tree] = await Promise.all([provider.getIndex(language), provider.getTree()]);
        const treeIndex = buildTreeIndex(tree);
        const byArtNr = buildIndexByArtNr(index);

        const products = index.filter((item) => item.type === 'product');
        const inTree = products.filter((item) => (item.nodeIds ?? []).some((id) => treeIndex.has(id)));
        const unknownNodeIds = new Set(
            index.flatMap((item) => item.nodeIds ?? []).filter((id) => !treeIndex.has(id))
        );

        let systems = 0;
        const referenced = new Set<string>();
        for (const { node } of treeIndex.values()) {
            if (node.type === 'system') {
                systems++;
            }
            for (const artNr of node.articleNumbers ?? []) {
                referenced.add(artNr);
            }
        }
        const missing = [...referenced].filter((artNr) => !byArtNr.has(artNr));

        const summary = {
            language,
            indexRows: index.length,
            products: products.length,
            parts: index.filter((item) => item.type === 'part').length,
            productsInTree: inTree.length,
            productsWithoutTreeNode: products.length - inTree.length,
            treeNodes: treeIndex.size,
            systems,
            articleNumbersReferencedByTree: referenced.size,
            referencedButNotInIndex: missing.length,
            nodeIdsNotInTree: unknownNodeIds.size
        };
        console.info('Index and tree - without a search the grid lists productsInTree (per node, see the next table):');
        console.table(summary);

        const counts = categoryCounts(index, treeIndex);
        const perRoot = tree.map((node) => ({
            node: node.name?.[language] ?? node.name?.de ?? node.nodeId,
            type: node.type,
            products: counts[node.nodeId] ?? 0
        }));
        console.info(
            `Products per top-level node (the badges in the tree). "Alle Produkte" lists all ${products.length} products; ` +
                'a product under several top-level nodes counts once per node, nodes with 0 are hidden:'
        );
        console.table([...perRoot, { node: 'Summe der Knoten', type: '', products: perRoot.reduce((sum, r) => sum + r.products, 0) }]);

        console.info('Catalog files this page loaded (articles/ and assets/ left out):');
        console.table(files);
        if (missing.length > 0) {
            console.info(`First article numbers the tree references but the index lacks: ${missing.slice(0, 10).join(', ')}`);
        }

        return { ...summary, perRoot, files, missingSample: missing.slice(0, 20) };
    };

    return {
        help,
        config,
        token,
        url,
        probe,
        index,
        check,
        list,
        listUrl,
        find,
        sample,
        trace,
        lastUrls: (count = 10) => recentAssetUrls(count),
        catalog: provider,
        api: (on?: boolean) => (on === undefined ? isApiLog() : setApiLog(on)),
        apiStats,
        apiCalls,
        stats
    };
};

declare global {
    interface Window {
        pih?: AssetDebugApi;
    }
}

/** Publishes the handle as `window.pih` - called once the provider exists (CatalogDataProvider). */
export const installAssetDebug = (provider: CatalogProvider): void => {
    if (typeof window === 'undefined') {
        return;
    }
    window.pih = buildApi(provider);
    console.info(
        `PIH asset diagnostics ready - type pih.help() for the available commands.${
            isAssetTrace() ? ' Asset trace is ON for this tab (pih.trace(false) to stop).' : ''
        }${isApiLog() ? ' API log is ON (pih.api(false) to stop).' : ''}`
    );
};
